import { useState, useMemo } from 'react';
import { Checkbox } from '../components/ui/checkbox';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Trash2, ChevronLeft, ChevronRight } from 'lucide-react';

export default function AdminTable({ data, columns, idField = 'id', onBulkDelete, bulkActions, emptyMessage = 'No data found' }) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const totalPages = Math.max(1, Math.ceil(data.length / pageSize));
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return data.slice(start, start + pageSize);
  }, [data, currentPage, pageSize]);

  // Reset page if data changes
  useMemo(() => {
    if (currentPage > Math.ceil(data.length / pageSize)) setCurrentPage(1);
  }, [data.length, pageSize, currentPage]);

  const allOnPageSelected = paginatedData.length > 0 && paginatedData.every(d => selectedIds.has(d[idField]));

  const toggleAll = () => {
    if (allOnPageSelected) {
      const newSet = new Set(selectedIds);
      paginatedData.forEach(d => newSet.delete(d[idField]));
      setSelectedIds(newSet);
    } else {
      const newSet = new Set(selectedIds);
      paginatedData.forEach(d => newSet.add(d[idField]));
      setSelectedIds(newSet);
    }
  };

  const toggleOne = (id) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) newSet.delete(id); else newSet.add(id);
    setSelectedIds(newSet);
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    if (onBulkDelete) {
      onBulkDelete(Array.from(selectedIds));
      setSelectedIds(new Set());
    }
  };

  if (data.length === 0) {
    return <div className="p-12 text-center text-[#A1A1AA]">{emptyMessage}</div>;
  }

  return (
    <div>
      {/* Bulk actions bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-2 bg-[#D4AF37]/10 border-b border-[#D4AF37]/20">
          <span className="text-[#D4AF37] text-sm font-medium">{selectedIds.size} selected</span>
          {onBulkDelete && (
            <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={handleBulkDelete} data-testid="bulk-delete-btn">
              <Trash2 className="h-3 w-3 mr-1" /> Delete Selected
            </Button>
          )}
          {bulkActions}
          <button className="text-[#A1A1AA] text-xs hover:text-white ml-auto" onClick={() => setSelectedIds(new Set())}>Clear selection</button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
        <Table style={{ minWidth: '800px' }}>
          <TableHeader>
            <TableRow className="border-[#2A2F3A]">
              <TableHead className="w-10">
                <Checkbox checked={allOnPageSelected} onCheckedChange={toggleAll} className="border-[#A1A1AA] data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]" />
              </TableHead>
              {columns.map(col => (
                <TableHead key={col.key} className="text-[#A1A1AA]">{col.label}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedData.map(row => (
              <TableRow key={row[idField]} className="border-[#2A2F3A]">
                <TableCell>
                  <Checkbox checked={selectedIds.has(row[idField])} onCheckedChange={() => toggleOne(row[idField])} className="border-[#A1A1AA] data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]" />
                </TableCell>
                {columns.map(col => (
                  <TableCell key={col.key} className={col.className || ''}>
                    {col.render ? col.render(row) : row[col.key] || '-'}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-[#2A2F3A]">
        <div className="flex items-center gap-2">
          <span className="text-[#A1A1AA] text-xs">Rows per page</span>
          <Select value={pageSize.toString()} onValueChange={v => { setPageSize(parseInt(v)); setCurrentPage(1); }}>
            <SelectTrigger className="w-16 h-7 bg-[#0B0C10] border-[#2A2F3A] text-white text-xs"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">
              {[10, 25, 50, 100].map(n => <SelectItem key={n} value={n.toString()} className="text-white text-xs">{n}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-[#A1A1AA] text-xs ml-2">{data.length} total</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[#A1A1AA] text-xs">Page {currentPage} of {totalPages}</span>
          <Button size="sm" variant="outline" className="h-7 w-7 p-0 border-[#2A2F3A] text-[#A1A1AA]" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)}>
            <ChevronLeft className="h-3 w-3" />
          </Button>
          <Button size="sm" variant="outline" className="h-7 w-7 p-0 border-[#2A2F3A] text-[#A1A1AA]" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>
            <ChevronRight className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}
